import React, { useEffect, useState } from "react";
import { Table, Space, Card, Button, Select, Row, Col, message, Tag } from "antd";
import { FileDownloadOutlined } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "Common/Footer";
import ReportService from "Services/AdminServices/ReportServices";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { LeaveTypeEnumLabel } from "Constants/Enums/LeavePolicyEnum";

const { Option } = Select;

function EmployeeOnLeaveReport() {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Fetch Departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await ReportService.getDepartments();
        const deptList = res.data || [];
        setDepartments(deptList);
        if (deptList.length > 0 && !selectedDepartment) {
          setSelectedDepartment(deptList[0].id);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        message.error("Failed to load departments.");
      }
    };
    fetchDepartments();
  }, []);

  //  Fetch Leave Data when filters change
  useEffect(() => {
    if (selectedDepartment) {
      fetchData(selectedDepartment, selectedMonth, selectedYear);
    }
  }, [selectedDepartment, selectedMonth, selectedYear]);

  const fetchData = async (departmentId, month, year) => {
    try {
      setLoading(true);

      const res = await ReportService.getLeaveReport(
        departmentId,
        month !== "" ? parseInt(month) + 1 : null,
        year || null
      );

      // Read defensively: some report endpoints now wrap the list as { period, data }.
      const body = res.data;
      const reportData = (body && body.data !== undefined ? body.data : body) || [];

      if (Array.isArray(reportData)) {
        const formattedData = reportData.map((item, index) => ({
          key: item.id || index,
          id: item.id,
          employeeName:
            `${item.employee?.fullName  || "Unknown"}`,
          email: item.employee?.email || "-",
          department: item.departmentName || "N/A",
          leaveType: LeaveTypeEnumLabel[item.leaveType] || "-",
          fromDate: item.fromDate ,
          toDate: item.toDate ,
          totalDays: item.totalDays || 0,
          reason: item.reason || "-",
          status: getStatusText(item.status),
          statusCode: item.status,
          reviewerRemarks: item.reviewerRemarks || "-",
          reviewedOn: item.reviewedOn || null,
          appliedDate:  item.createdOn,
        }));

        setData(formattedData);
      } else {
        setData([]);
        message.warning("No leave records found for the selected criteria.");
      }
    } catch (error) {
      console.error("Error fetching leave report:", error);
      console.error("Error details:", error.response?.data);
      message.error(
        `Failed to load leave report: ${error.response?.data?.message || error.message}`
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  //  Helper for status
  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return "Pending";
      case 1:
        return "Approved";
      case 2:
        return "Rejected";
      case 3:
        return "Cancelled";
      default:
        return "Unknown";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "orange";
      case "Approved":
        return "green";
      case "Rejected":
        return "red";
      case "Cancelled":
        return "default";
      default:
        return "default";
    }
  };

  //  Download PDF
  const handleDownloadPDF = () => {
    if (!data || data.length === 0) {
      message.warning("No data available to generate PDF");
      return;
    }

    try {
      const doc = new jsPDF();

      // Title
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text("EMPLOYEE LEAVE REPORT", 105, 20, { align: "center" });

      // Report info
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);

      let currentY = 35;
      const departmentName =
        departments.find((d) => d.id === selectedDepartment)?.name || "Selected Department";

      doc.text(`Department: ${departmentName}`, 14, currentY);
      currentY += 6;

      if (selectedMonth !== "" || selectedYear) {
        const monthText = selectedMonth !== "" ? monthNames[selectedMonth] : "";
        const yearText = selectedYear || "";
        doc.text(`Period: ${monthText} ${yearText}`.trim(), 14, currentY);
        currentY += 6;
      }

      doc.text(`Total Records: ${data.length}`, 14, currentY);
      currentY += 6;
      doc.text(`Generated: ${moment().format("MMMM DD, YYYY HH:mm")}`, 14, currentY);
      currentY += 15;

     const headers = [
  "Employee",
  "Department",
  "Leave Type",
  "Leave Period", // merged column
  "Days",
  "Status",
  "Reason",
];

const tableData = data.map((item) => [
  item.employeeName,
  item.department,
  item.leaveType,
  `${moment(item.fromDate).format("MMM DD, YYYY")} - ${moment(item.toDate).format("MMM DD, YYYY")}`, // merged
  item.totalDays.toString(),
  item.status,
  item.reason?.substring(0, 30) + (item.reason?.length > 30 ? "..." : "") || "-",
]);

      // Generate table
   doc.autoTable({
  startY: currentY,
  head: [headers],
  body: tableData,
  theme: "grid",
  styles: {
    fontSize: 8,
    cellPadding: 3,
    halign: 'center', // center text in all cells
  },
  headStyles: {
    fillColor: [70, 130, 180],
    textColor: 255,
    fontStyle: "bold",
    fontSize: 9,
    halign: 'center',
  },
  alternateRowStyles: { fillColor: [248, 248, 248] },
  columnStyles: {
    0: { cellWidth: 25 },
    1: { cellWidth: 25 },
    2: { cellWidth: 15 },
    3: { cellWidth: 46 },
    4: { cellWidth: 14 },
    5: { cellWidth: 19 },
    6: { cellWidth: 40 },
  },
  tableWidth: 'auto', // auto-fit within page
});

      // Add summary
      const finalY = doc.lastAutoTable.finalY + 10;
      const approvedCount = data.filter((item) => item.status === "Approved").length;
      const pendingCount = data.filter((item) => item.status === "Pending").length;
      const rejectedCount = data.filter((item) => item.status === "Rejected").length;

      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.setTextColor(40, 40, 40);
      doc.text(
        `Summary: ${approvedCount} Approved, ${pendingCount} Pending, ${rejectedCount} Rejected, ${data.length} Total`,
        14,
        finalY
      );

      const filename = `Leave-Report-${departmentName.replace(/\s+/g, "-")}-${moment().format(
        "YYYYMMDD"
      )}.pdf`;
      doc.save(filename);
      message.success("PDF report downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      message.error("Failed to generate PDF report");
    }
  };

  //  Table Columns
  const columns = [
  {
    title: "Employee",
    dataIndex: "employeeName",
    key: "employeeName",
    sorter: (a, b) => a.employeeName.localeCompare(b.employeeName),
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
  },
  {
    title: "Department",
    dataIndex: "department",
    key: "department",
    sorter: (a, b) => a.department.localeCompare(b.department),
  },
  {
    title: "Leave Type",
    dataIndex: "leaveType",
    key: "leaveType",
  },
  {
    title: "Leave Period",
    key: "leavePeriod",
    render: (_, record) =>
      `${moment(record.fromDate).format("MMM DD, YYYY")} - ${moment(record.toDate).format("MMM DD, YYYY")}`,
    sorter: (a, b) => moment(a.fromDate).valueOf() - moment(b.fromDate).valueOf(),
  },
  {
    title: "Days",
    dataIndex: "totalDays",
    key: "totalDays",
    sorter: (a, b) => a.totalDays - b.totalDays,
  },
  {
    title: "Reason",
    dataIndex: "reason",
    key: "reason",
    ellipsis: true,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    filters: [
      { text: "Pending", value: "Pending" },
      { text: "Approved", value: "Approved" },
      { text: "Rejected", value: "Rejected" },
    ],
    onFilter: (value, record) => record.status === value,
  },
  {
    title: "Applied Date",
    dataIndex: "appliedDate",
    key: "appliedDate",
    render: (date) => (date ? moment(date).format("MMM DD, YYYY") : "N/A"),
    sorter: (a, b) => moment(a.appliedDate || 0).valueOf() - moment(b.appliedDate || 0).valueOf(),
  },
];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        className="card-professional"
        title="Employee On Leave Report"
        extra={<Button onClick={() => window.history.back()}>Back</Button>}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space size="middle">
              <Select
                value={selectedDepartment}
                style={{ width: 200 }}
                onChange={setSelectedDepartment}
                placeholder="Select Department"
                loading={departments.length === 0}
              >
                {departments.map((d) => (
                  <Option key={d.id} value={d.id}>
                    {d.name}
                  </Option>
                ))}
                {/* Executives: backend filters by Executive designation on -1. */}
                <Option value={-1}>Executives</Option>
              </Select>

              <Select
                value={selectedMonth}
                onChange={setSelectedMonth}
                style={{ width: 150 }}
                placeholder="Select Month"
              >
                <Option value="">All Months</Option>
                {monthNames.map((month, index) => (
                  <Option key={index} value={index}>
                    {month}
                  </Option>
                ))}
              </Select>

              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: 120 }}
                placeholder="Select Year"
              >
                {years.map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>

          <Col>
            <Button
              className="btn-primary"
              icon={<FileDownloadOutlined />}
              onClick={handleDownloadPDF}
              disabled={!data.length}
            >
              Download PDF
            </Button>
          </Col>
        </Row>

        <div style={{ marginBottom: 16, color: "#666" }}>
          Showing {data.length} records
          {selectedDepartment &&
            ` for ${departments.find((d) => d.id === selectedDepartment)?.name}`}
        </div>

        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: 1200 }}
          rowKey="key"
        />
      </Card>
      <Footer />
    </DashboardLayout>
  );
}

export default EmployeeOnLeaveReport;
